import { Modal } from "antd";
import dayjs from "dayjs";
import { Formik } from "formik";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { DATE_FORMAT } from "utils/constants";
import { getMinDeltaStart } from "utils/helpers/array";
import { bindDateInputProps } from "utils/helpers/input";

const MIN_DELTA_START_RIA = 34;

const ExpirationModal = ({ project }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const [open, setOpen] = useState(false);
  const [deltaRia, setDeltaRia] = useState(MIN_DELTA_START_RIA);
  const [deltaStart, setDeltaStart] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setOpen(!!get(project, "expired") && !get(project, "locked"));
  }, [project]);

  useQuery(INSURANCE_PLANS, {
    variables: { where: {} },
    onCompleted: ({ insurancePlans: { data: insurancePlans } }) => {
      const ria = getMinDeltaStart({ insurancePlans, minValue: 30 });
      const start = getMinDeltaStart({
        insurancePlans,
        minValue: 1,
        key: "DELTA_START",
      });
      setDeltaRia(Math.max(ria, MIN_DELTA_START_RIA));
      setDeltaStart(start);
    },
  });

  const [updateProject] = useMutation(UPDATE_PROJECT);

  const onValidate = ({ contract }) => {
    setLoading(true);
    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contract: {
            id: get(project, "contract.id"),
            issuanceDate: get(contract, "issuanceDate"),
          },
          expired: false,
        },
      },
      onCompleted: () => window.location.reload(),
      onError: () => setLoading(false),
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ria: get(project, "ria", false),
        contract: { issuanceDate: get(project, "contract.issuanceDate") },
      }}
      onSubmit={onValidate}
    >
      {({ values, handleSubmit, ...formProps }) => (
        <Modal
          open={open}
          onCancel={() => setOpen(false)}
          width={500}
          footer={null}
        >
          <div className="expiration_modal">
            <span className="expiration_modal--icon">
              <HiOutlineExclamationCircle size={32} />
            </span>
            <span className="expiration_modal--title">
              {DETAIL.EXPIRATION_MODAL.TITLE}
            </span>
            <span className="expiration_modal--description">
              {DETAIL.EXPIRATION_MODAL.DESCRIPTION}
            </span>
            <div className="expiration_modal--date">
              <Input
                type="date"
                variant="simple"
                format={DATE_FORMAT}
                disabledDate={(current) =>
                  current && values.ria
                    ? current < dayjs().startOf("day").add(deltaRia, "days")
                    : current < dayjs().startOf("day").add(deltaStart, "days")
                }
                defaultPickerValue={
                  !get(values, "contract.issuanceDate") && dayjs()
                }
                {...bindDateInputProps({
                  name: "contract.issuanceDate",
                  values,
                  ...formProps,
                })}
              />
            </div>
            <div className="expiration_modal--actions">
              <Button type="gray--outlined" onClick={() => setOpen(false)}>
                {DETAIL.EXPIRATION_MODAL.EXIT}
              </Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={loading || !get(values, "contract.issuanceDate")}
              >
                {DETAIL.EXPIRATION_MODAL.VALIDATE}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Formik>
  );
};

export default ExpirationModal;
